import { PaginatedResponse } from "@/interfaces/paginatedResponse";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { ref } from "vue";
import { ToastInterface, useToast } from "vue-toastification";
import { HotelGroups } from "../../../../interfaces/HotelGroup";

interface Props {
  immediate?: boolean;
  queryKey?: string;
}

interface Params {
  page: number | string;
}

interface CreateParams {
  name: string;
}

interface UpdateParams {
  id: number;
  name: string;
}

const getHotelGroups = async (
  params: Params,
): Promise<PaginatedResponse<HotelGroups>> => {
  const { data } = await axios.get(
    `/api/internal/hotel-groups?page=${params.page}`,
  );

  return data;
};

const createHotelGroup = async (
  params: CreateParams,
  toast: ToastInterface,
) => {
  try {
    const { data } = await axios.post("/api/internal/hotel-groups", params);

    toast.success("Grupo creado correctamente");
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const updateHotelGroup = async (
  params: UpdateParams,
  toast: ToastInterface,
) => {
  try {
    const { data } = await axios.put(
      `/api/internal/hotel-groups/${params.id}`,
      {
        name: params.name,
      },
    );

    toast.success("Grupo actualizado correctamente");
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const deleteHotelGroup = async (id: number, toast: ToastInterface) => {
  try {
    const { data } = await axios.delete(`/api/internal/hotel-groups/${id}`);

    toast.success("Grupo desactivada correctamente");
    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const restoreHotelGroup = async (id: number, toast: ToastInterface) => {
  try {
    const { data } = await axios.post(
      `/api/internal/hotel-groups/${id}/restore`,
    );

    toast.success("Grupo activada correctamente");
    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const useHotelGroups = (props: Props) => {
  const { immediate = true } = props;
  const page = ref<number | string>(1);
  const toast = useToast();

  const { data, isError, isLoading, refetch } = useQuery(
    ["hotelGroups", props.queryKey],
    () =>
      getHotelGroups({
        page: page.value,
      }),
    {
      enabled: immediate,
    },
  );

  const createMutation = useMutation((name: string) =>
    createHotelGroup({ name }, toast),
  );

  const updateMutation = useMutation((params: UpdateParams) =>
    updateHotelGroup(params, toast),
  );

  const deleteMutation = useMutation((id: number) =>
    deleteHotelGroup(id, toast),
  );

  const restoreMutation = useMutation((id: number) =>
    restoreHotelGroup(id, toast),
  );

  return {
    data,
    page,
    isError,
    isLoading,
    refetch,

    createMutation,
    createMutationLoading: createMutation.isLoading,
    createMutationError: createMutation.isError,
    createMutationSuccess: createMutation.isSuccess,
    createMutationAsync: createMutation.mutateAsync,

    updateMutation,
    updateMutationLoading: updateMutation.isLoading,
    updateMutationError: updateMutation.isError,
    updateMutationSuccess: updateMutation.isSuccess,
    updateMutationAsync: updateMutation.mutateAsync,

    deleteMutation,
    deleteMutationLoading: deleteMutation.isLoading,
    deleteMutationError: deleteMutation.isError,
    deleteMutationSuccess: deleteMutation.isSuccess,
    deleteMutationAsync: deleteMutation.mutateAsync,

    restoreMutation,
    restoreMutationLoading: restoreMutation.isLoading,
    restoreMutationError: restoreMutation.isError,
    restoreMutationSuccess: restoreMutation.isSuccess,
    restoreMutationAsync: restoreMutation.mutateAsync,
  };
};

export default useHotelGroups;
